import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  TableCellLayout,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens
} from '@fluentui/react-components';
import { Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle } from '@fluentui/react-components/unstable';
import { Dismiss24Regular, Add24Regular, Delete24Regular, Edit24Regular, Tag24Regular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../../redux/reducers';
import { ServiceCategory } from '../../../models/Entities/ServiceCategory';
import ServiceCategoryFormComponent from './ServiceCategory.Form.Component';
import ConfirmationModalComponent from '../../../commons/component/ConfirmationModal/ConformationModal.Component';

// i18Next
import { useTranslation } from 'react-i18next';

// store
import * as ServiceCategoryStore from '../../../redux/middleware/ServiceCategoryMiddleware';

export interface IProps {
  isOpen: boolean;
  onDismiss: () => void;
}

export const ServiceCategoryListComponent = (props: IProps) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const styles = useStyles();

  const ServiceCategoryState = useSelector((appState: AppState) => appState.ServiceCategories);

  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | undefined>();

  useEffect(() => {
    if (!ServiceCategoryState?.successLoadingAll && !ServiceCategoryState?.isLoadingAll) {
      dispatch(ServiceCategoryStore.actionCreators.GetAllServiceCategories());
    }
  }, []);

  // Reload list after successful add, update or delete
  useEffect(() => {
    if (
      ServiceCategoryState?.isAddingSuccess ||
      ServiceCategoryState?.isSavingSuccess ||
      ServiceCategoryState?.isDeletingSuccess
    ) {
      dispatch(ServiceCategoryStore.actionCreators.GetAllServiceCategories());
    }
  }, [
    ServiceCategoryState?.isAddingSuccess,
    ServiceCategoryState?.isSavingSuccess,
    ServiceCategoryState?.isDeletingSuccess
  ]);

  const handleAddNew = () => {
    setSelectedCategory(undefined);
    setIsFormOpen(true);
  };

  const handleEdit = (category: ServiceCategory) => {
    setSelectedCategory(category);
    setIsFormOpen(true);
  };

  const handleFormDismiss = () => {
    setIsFormOpen(false);
    setSelectedCategory(undefined);
  };

  const categories = ServiceCategoryState?.list || [];

  return (
    <>
      <Drawer
        open={props.isOpen}
        onOpenChange={(_, { open }) => !open && props.onDismiss()}
        position="end"
        size="medium"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<Dismiss24Regular />}
                onClick={props.onDismiss}
              />
            }
          >
            {t('serviceCategoryList.title')}
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody>
          <div className={styles.container}>
            <div className={styles.actionBar}>
              <Button
                appearance="primary"
                icon={<Add24Regular />}
                onClick={handleAddNew}
              >
                {t('serviceCategoryList.addButton')}
              </Button>
            </div>

            {ServiceCategoryState?.isLoadingAll && (
              <div className={styles.loadingContainer}>
                <Spinner label={t('serviceCategoryList.loading')} />
              </div>
            )}

            {ServiceCategoryState?.failOnLoadingAll && ServiceCategoryState?.error && (
              <MessageBar intent="error">
                <MessageBarBody>
                  {ServiceCategoryState.error.ErrorMessage}
                </MessageBarBody>
              </MessageBar>
            )}

            {ServiceCategoryState?.isDeleting && (
              <MessageBar intent="info">
                <MessageBarBody>
                  {t('serviceCategoryList.deleting')}
                </MessageBarBody>
              </MessageBar>
            )}

            {ServiceCategoryState?.isDeletingSuccess && (
              <MessageBar intent="success">
                <MessageBarBody>
                  {t('serviceCategoryList.deleteSuccess')}
                </MessageBarBody>
              </MessageBar>
            )}
            {ServiceCategoryState?.isAddingSuccess && (
              <MessageBar intent="success">
                <MessageBarBody>
                  {t('serviceCategoryList.addSuccess')}
                </MessageBarBody>
              </MessageBar>
            )}

            {ServiceCategoryState?.isSavingSuccess && (
              <MessageBar intent="success">
                <MessageBarBody>
                  {t('serviceCategoryList.updateSuccess')}
                </MessageBarBody>
              </MessageBar>
            )}
            {categories.length > 0 && (
              <Table aria-label="Service Categories">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>{t('serviceCategoryList.columnDescription')}</TableHeaderCell>
                    <TableHeaderCell>{t('serviceCategoryList.columnActions')}</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((category: ServiceCategory) => (
                    <TableRow key={category.id}>
                      <TableCell>
                        <TableCellLayout media={<Tag24Regular />}>
                          {category.description || category.id}
                        </TableCellLayout>
                      </TableCell>
                      <TableCell>
                        <TableCellLayout>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              appearance="subtle"
                              icon={<Edit24Regular />}
                              onClick={() => handleEdit(category)}
                              disabled={ServiceCategoryState?.isDeleting}
                            >
                              {t('serviceCategoryList.editButton')}
                            </Button>
                            <ConfirmationModalComponent
                              onDismiss={() => {}}
                              onConfirm={() => dispatch(ServiceCategoryStore.actionCreators.DeleteServiceCategory(category.id))}
                              title={t('serviceCategoryList.deleteButton')}
                              message={t('serviceCategoryList.confirmDelete')}
                              buttonApparence="subtle"
                              buttonShape={undefined}
                              buttonTitle={t('serviceCategoryList.deleteButton')}
                              buttonIcon={<Delete24Regular />}
                              isProcessing={ServiceCategoryState?.isDeleting}
                            />
                          </div>
                        </TableCellLayout>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {!ServiceCategoryState?.isLoadingAll && categories.length === 0 && (
              <MessageBar intent="info">
                <MessageBarBody>
                  {t('serviceCategoryList.noCategories')}
                </MessageBarBody>
              </MessageBar>
            )}
          </div>
        </DrawerBody>
      </Drawer>

      {isFormOpen && (
        <ServiceCategoryFormComponent
          isOpen={isFormOpen}
          dismissPanel={handleFormDismiss}
          ServiceCategory={selectedCategory}
        />
      )}
    </>
  );
};

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalL
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacingVerticalXL
  },
  categoryLink: {
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline'
    }
  }
});

export default ServiceCategoryListComponent;
